import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Send, Users } from "lucide-react";

export default function AdminWaitlistTable({ canInvite = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/waitlist/admin/list");
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (row) => {
    setInviting(row.id);
    try {
      const { data } = await api.post(`/waitlist/admin/invite/${row.id}`);
      const link = data.invite_link || `${window.location.origin}${data.invite_link_path}`;
      await navigator.clipboard.writeText(link);
      toast.success(`Invite sent to ${data.email} · link copied`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setInviting(null);
    }
  };

  const copyLink = async (row) => {
    const link = `${window.location.origin}/register?invite=${row.invite_token}`;
    await navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  return (
    <div data-testid="admin-waitlist-table" className="rounded-xl border border-[#27272A] bg-[#121212] p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Waitlist</div>
          <h3 className="font-black text-xl tracking-tight mt-1 inline-flex items-center gap-2">
            <Users className="h-5 w-5 text-neutral-400" /> {rows.length} pre-launch signups
          </h3>
        </div>
      </div>
      {loading ? (
        <div className="text-neutral-500 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-neutral-500 text-sm">No one on the waitlist yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="text-left py-2 pr-3">#</th>
                <th className="text-left py-2 pr-3">Email</th>
                <th className="text-left py-2 pr-3">Name</th>
                <th className="text-left py-2 pr-3">Source</th>
                <th className="text-left py-2 pr-3">Joined</th>
                <th className="text-left py-2 pr-3">Invited</th>
                <th className="text-right py-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#27272A]">
                  <td className="py-2 pr-3 font-mono text-neutral-500">#{r.position}</td>
                  <td className="py-2 pr-3 truncate max-w-[220px]">{r.email}</td>
                  <td className="py-2 pr-3 text-neutral-400">{r.name || "—"}</td>
                  <td className="py-2 pr-3 text-neutral-500 text-xs">{r.source || "—"}</td>
                  <td className="py-2 pr-3 text-neutral-500 text-xs">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {r.invited_at ? (
                      <span className="text-[#34C759]">{new Date(r.invited_at).toLocaleDateString()}</span>
                    ) : (
                      <span className="text-neutral-500">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      {r.invite_token && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`waitlist-copy-${r.id}`}
                          onClick={() => copyLink(r)}
                          className="rounded-full text-xs h-8 px-3 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white"
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                      )}
                      {canInvite && (
                      <Button
                        size="sm"
                        data-testid={`waitlist-invite-${r.id}`}
                        onClick={() => invite(r)}
                        disabled={inviting === r.id}
                        className="rounded-full text-xs h-8 px-3 bg-white text-black hover:bg-neutral-200"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {inviting === r.id ? "Sending…" : r.invite_token ? "Resend" : "Invite"}
                      </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
